import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import { Divider } from "@mui/material";
import cx from "classnames";
import FabButtonMenuItem from "./components/FabButtonMenuItem/FabButtonMenuItem";
import { COLLAPSED_HOVER_GROW_CLASS } from "./FabButton.constants";
import type { FabMenuSection } from "./FabButton.state";
import type { Row } from "./FabButton.types";

const renderMenuItem = (
  item: FabMenuSection["items"][number],
  expanded: boolean
): Row => ({
  key: item.id,
  node: <FabButtonMenuItem item={item} expanded={expanded} />,
});

const sectionToRows = (section: FabMenuSection, expanded: boolean): Row[] => {
  const rows: Row[] = [];
  if (section.header) {
    rows.push({
      key: `header-${section.id}`,
      node: (
        <div
          className={cx(
            "px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider",
            expanded ? "text-text-secondary" : "text-white/40"
          )}
        >
          {section.header}
        </div>
      ),
    });
  }
  for (const item of section.items) {
    rows.push(renderMenuItem(item, expanded));
  }
  return rows;
};

const buildFeedbackSection = (onFeedbackClick: () => void): FabMenuSection => ({
  id: "feedback",
  items: [
    {
      id: "feedback",
      label: "Feedback",
      icon: <FeedbackOutlinedIcon fontSize="small" />,
      onClick: onFeedbackClick,
    },
  ],
});

type BuildRowsArgs = {
  pageSections: FabMenuSection[];
  open: boolean;
  onFeedbackClick: () => void;
};

export const buildRows = ({
  pageSections,
  open,
  onFeedbackClick,
}: BuildRowsArgs): Row[] => {
  const rows: Row[] = [];

  for (const section of pageSections) {
    rows.push(...sectionToRows(section, open));
  }

  // Divider only when real rows precede the fixed Feedback section.
  if (rows.length > 0) {
    rows.push({ key: "divider-fixed", node: <Divider className="my-1.5" /> });
  }
  rows.push(...sectionToRows(buildFeedbackSection(onFeedbackClick), open));

  return rows;
};

// Single-element morph: the dark circle reveals into the panel via an expanding
// clip-path circle anchored to the bottom-right (under the trigger). clip-path
// is used instead of width/height so the reveal adapts to any menu height (the
// drop-shadow lives on the outer wrapper because clip-path would clip a box-shadow).
// radius/origin derive from the numeric FAB size so the disc and trigger shrink
// together; transformOrigin matches the clip origin so the hover grow scales about
// the disc centre, staying aligned with the trigger.
export const getPanelClip = (
  fabSize: number,
  open: boolean
): { clipPath: string; transformOrigin: string } => {
  const half = fabSize / 2;
  const transformOrigin = `calc(100% - ${half}px) calc(100% - ${half}px)`;
  const clipPath = open
    ? `circle(200% at ${transformOrigin})`
    : `circle(${half}px at ${transformOrigin})`;
  return { clipPath, transformOrigin };
};

export const getPanelStateClass = (open: boolean): string =>
  open
    ? "border-divider bg-background-paper text-text-primary easing-spring-soft duration-600"
    : `border-white/10 bg-primary-dark text-white easing-spring-soft duration-500 ${COLLAPSED_HOVER_GROW_CLASS}`;
